import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { MYBESTUSER_API } from "../../config/environment";
// Same folder, so this is a cycle (index.js imports us back). It is safe:
// useSignout is only ever *called* at render time, by when the live binding
// has resolved — we never touch it at module-evaluation time.
import { useSignout } from "./index";
import { clearSchoolClassroomData } from "../schoolClassroomStorage";

// Disconnect guard only: how long we wait before giving up on the hidden
// iframe entirely (network down / page never loaded). The authenticated vs.
// not-authenticated decision is EVENT-DRIVEN — /api/sso/auto-login always
// posts a message back (code or error), so a slow network just means we wait
// a little longer; it can never produce a wrong "not logged in" verdict.
const DISCONNECT_TIMEOUT_MS = 15000;

// Which mybestuser account the session we last took belonged to. Our own
// bookkeeping: the id the tablet's SSO session reported, which is the only
// thing comparable to what /api/sso/auto-login reports next time.
const SSO_USER_KEY = "ssoMasterUserId";

const rememberSSOUser = (userId) => {
  try {
    if (userId) localStorage.setItem(SSO_USER_KEY, String(userId));
  } catch (e) {
    // storage blocked — we simply re-take the session next time
  }
};

// Drop the marker when the tablet has logged out. Must run whenever we clear
// our own login state: useSignout does not touch this key, and a marker left
// behind would make the SAME user's next tablet login look like "already this
// user", short-circuiting the exchange while the token is already gone.
const forgetSSOUser = () => {
  try {
    localStorage.removeItem(SSO_USER_KEY);
  } catch (e) {
    // storage blocked — nothing to forget
  }
};

// True on the customized-tablet flow. Mirrors how the rest of the app
// detects tablet mode (MyBestUserLogin): the flag persisted by an earlier
// visit, or an explicit marker in the URL.
const inTabletMode = () => {
  try {
    if (localStorage.getItem("isTablet") === "true") return true;
  } catch (e) {
    // storage blocked (third-party context) — fall through to the URL
  }
  return window.location.href.includes("isTablet=true");
};

// Whether to keep this page in step with the mybestuser session even while
// signed in. Tablet mode says yes, but that flag only exists once something
// has put isTablet=true in the URL — nothing in the SSO flow does, so on a
// browser that has only ever arrived through it the flag is simply absent
// (which is what stopped a freshly imaged tablet from following the account
// switch, while a well-travelled one worked). Having taken a session before
// is the more direct evidence: this browser follows mybestuser, so it
// should keep following it.
export const shouldTrackSession = () => {
  try {
    if (localStorage.getItem(SSO_USER_KEY)) return true;
  } catch (e) {
    // storage blocked — fall back to the URL/flag check
  }
  return inTabletMode();
};

// Whether this page runs in the customized tablet's MDM browser, whose UA
// carries "AgentWeb/" (measured 2026-10-12: "...; wv) ... Chrome/133 ...
// AgentWeb/5.0.0 UCBrowser/11.6.4.950"); mybestuser's tabletModeHelpers uses
// the same test. A plain Android-WebView (`wv`) test is too wide: our own
// Capacitor APK and in-app browsers such as WeChat or LINE report `wv` too,
// and none of them use SSO. The isTablet flag cannot answer this either: PC
// users set it on purpose to see tablet-only resources. Should the vendor
// ever drop the marker, SSO auto-login stops and the tablet signs in through
// the dialog like any other browser.
export const inTabletBrowser = () =>
  typeof navigator !== "undefined" &&
  /Android/i.test(navigator.userAgent) &&
  /\bAgentWeb\//.test(navigator.userAgent);

// The tablet's sign-in app (tablet-auth-app) owns every sign-in and sign-out
// on the tablet. Our own sign-out would only drop this page's token: the
// mybestuser session the app's bridge planted in this browser lives on, and
// the silent auto-login below signs the page straight back in. So on the
// tablet a sign-out button opens the app's account card instead
// (tabletauth://open?intent=logout). The user decides there: its Logout
// button ends the session for the browser too (the recheck below then signs
// this page out when they come back), and going back without pressing it
// leaves everything as it was. `redirect` names this page, so a sign-in
// after that Logout hands the browser back here.
export const tabletAuthLogoutUrl = (returnTo) =>
  "tabletauth://open?intent=logout&redirect=" + encodeURIComponent(returnTo);

// Opens the app's account card when this page runs in the tablet browser;
// returns false everywhere else so the caller signs out the usual way (PC
// sign-out is untouched by design).
export const openTabletAuthForLogout = () => {
  if (!inTabletBrowser()) return false;
  window.location.href = tabletAuthLogoutUrl(window.location.href);
  return true;
};

// Whether our token came from the tablet's SSO session: the marker is only
// written after /api/sso/auto-login answered with a session, and only
// tablet-auth-app's bridge creates those sessions.
const tokenFromTabletSession = () => {
  try {
    return Boolean(localStorage.getItem(SSO_USER_KEY));
  } catch (e) {
    return false;
  }
};

// When our stored token was issued (ms since epoch), read from its JWT
// payload; null when there is no token or it does not parse. The token is
// the mybestuser JWT signinWithCode handed back, so `iat` is server time —
// comparable with the session's createdAt, which the same server stamped.
const tokenIssuedAtMs = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const { iat } = JSON.parse(atob(base64));
    return typeof iat === "number" ? iat * 1000 : null;
  } catch (e) {
    return null;
  }
};

// Whether our token is older than the tablet session it is compared with.
// The same user signing in again on the tablet stamps loggedOutAt first,
// and mybestuser then refuses every token issued before it: the identity
// still matches, but our token is dead (GraphQL answers `me: null`), so
// "same user" alone is not enough to keep it. A token is normally issued a
// few seconds AFTER the session (bridge, then our exchange); the margin
// covers `iat` being whole seconds while createdAt has milliseconds.
const STALE_TOKEN_MARGIN_MS = 5000;
const tokenPredatesSession = (sessionCreatedAt) => {
  if (typeof sessionCreatedAt !== "number") return false;
  const issuedAt = tokenIssuedAtMs();
  if (issuedAt === null) return false;
  return issuedAt + STALE_TOKEN_MARGIN_MS < sessionCreatedAt;
};

// Whether mybestuser still accepts our token, for a server that does not
// report sessionCreatedAt. /api/user/profile answers exactly that: 200 when
// the credential identifies someone, 401 when it no longer does (the state
// loggedOutAt puts a token in). Only a 401 counts as rejection — a network
// error or a 500 says nothing about the token, and dropping a good session
// on a flaky connection would be worse than the stale one this guards
// against.
const tokenStillAccepted = async () => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    const response = await fetch(MYBESTUSER_API + "/api/user/profile", {
      headers: { Authorization: "Bearer " + token }
    });
    return response.status !== 401;
  } catch (e) {
    return true;
  }
};

// Whether this page still holds a token the app would treat as signed in.
const hasLiveTokenNow = () => {
  const token = localStorage.getItem("token");
  const expiresAt = Number(localStorage.getItem("expiresAt"));
  return Boolean(token && expiresAt && Date.now() < expiresAt);
};

// The one decision the effect below makes before doing anything, pulled out
// so the initial state can be computed synchronously: TabletLandingPage
// reads that state in its own mount effect, and a child's effect runs before
// its parent's, so a value that only became right inside our effect would
// arrive one render too late.
const shouldAttempt = () => {
  if (!MYBESTUSER_API) return false;
  // Tablet SSO only. Everywhere else — PC browsers, the TA Capacitor APK,
  // in-app browsers — sign-in and sign-out stay exactly as they were before
  // SSO: no iframe, no
  // verdict, and above all no automatic sign-out. PC users legitimately keep
  // isTablet=true to see tablet-only resources; that flag must keep meaning
  // only that. Without this guard a signed-in PC user with the flag was
  // signed out on every page load (the session check answers
  // not_authenticated: a PC never has a tablet session).
  if (!inTabletBrowser()) return false;
  // An unexpired token normally means "signed in — nothing to do". An
  // expired one is treated as absent so a still-valid tablet session
  // refreshes it silently. In tablet mode we go on even with a live
  // token, to detect that the tablet now belongs to someone else.
  if (hasLiveTokenNow() && !shouldTrackSession()) return false;
  // Already exchanging a code (manual dialog flow) — don't race it.
  if (window.location.pathname.startsWith("/mybestuser")) return false;
  return true;
};

// Where to send the user once a handover has signed them in again. The
// handover reloads through /mybestuser, whose only memory of where the user
// was is sessionStorage "previousURL" — and nothing has written it, because
// Authorization only records the path for visitors it turns away, and this
// user was let in. Without it MyBestUserLogin falls back to the role home,
// which is how a tablet opened on /student/teachingAssistant ended up on
// /student. The landing page is left unrecorded on purpose: "/" would take
// precedence over the role home, and a signed-in user belongs on the latter.
const rememberCurrentLocation = () => {
  const { pathname, search } = window.location;
  if (pathname === "/" || pathname.startsWith("/mybestuser")) return;
  try {
    sessionStorage.setItem("previousURL", pathname + search);
  } catch (e) {
    // storage blocked — MyBestUserLogin falls back to the role home
  }
};

// Silent SSO auto-login for the customized-tablet flow, implementing
// "Phase 2: TA Web Auto-Login" of MasterAccountServer
// docs/SSO_IMPLEMENTATION_PLAN.md:
//
//   1. tablet-auth signs the user in once and its bridge plants a
//      mybestuser session cookie in the MDM (Launcher) browser.
//   2. When TA Web opens, we load a hidden iframe of
//      MYBESTUSER_API/api/sso/auto-login. On mybestuser's origin the browser
//      attaches that session cookie automatically.
//   3. The page posts { type: "mybestuser-sso", code, userId } back to us.
//      With no stored token we hand the one-time code to the existing
//      /mybestuser?code= route, so the signinWithCode exchange, role
//      routing, previousURL restore and isTablet handling all stay in that
//      single place.
//   4. On a timeout, or any error other than not_authenticated, we clean up
//      and do nothing — the manual sign-in dialog keeps working as before.
//      A not_authenticated verdict on a session-tracking browser is the one
//      exception: it means the tablet logged out, so we clear this page's
//      own login state and reload (see the handler for the order and guard).
//
// Account handover (shared tablets): our token lives in this origin's
// localStorage and nothing clears it when the tablet switches users, so a
// still-valid token used to freeze this page on the PREVIOUS account — the
// tablet says boss, the page keeps operating as admin. In tablet mode we
// therefore attempt the iframe even when a live token exists and compare
// the session's userId against the stored one: same user → drop the code
// and change nothing; different user → hand over. The handover navigates
// with a full page load (not SPA navigate) on purpose: Apollo's cache and
// Redux still hold the previous account's data, and a reload is what
// guarantees none of it leaks into the new session. Outside tablet mode a
// live token still short-circuits everything, exactly as before.
//
// Returns the attempt's verdict, so a page that would otherwise start a
// sign-in of its own can wait for it:
//   "pending" — the iframe is out; the answer is on its way.
//   "success" — a code came back and we are navigating to /mybestuser.
//   "none"    — no sign-in is coming from here (never attempted, session
//               absent, or the same user is already signed in).
// TabletLandingPage auto-opens the authorize dialog for a signed-out visitor,
// and that dialog signs in through the very same cookie — so with no verdict
// to wait for, the two flows raced each other through /mybestuser. Whichever
// finished second found sessionStorage "previousURL" already consumed by the
// first, fell back to the role home, and (being the dialog) forced a full
// reload there, overriding the correct SPA navigation the first had made.
//
// The hook is self-contained on purpose (only needs MYBESTUSER_API and the
// /mybestuser route) so StudentAppWebClient can adopt the same file later.
export const useSSOAutoLogin = () => {
  const navigate = useNavigate();
  // The message handler lives in a mount-only effect; keep the latest signout
  // in a ref so it can call the current one without re-subscribing.
  const signout = useSignout();
  const signoutRef = useRef(signout);
  signoutRef.current = signout;
  // Synchronous on purpose (see shouldAttempt): the first render must
  // already say "pending" or the landing page opens its dialog anyway.
  const [state, setState] = useState(() =>
    shouldAttempt() ? "pending" : "none"
  );

  useEffect(() => {
    if (!shouldAttempt()) return undefined;

    const expectedOrigin = new URL(MYBESTUSER_API).origin;

    // Cleanup of the attempt in flight, or null between attempts. Doubles as
    // the "already asking" guard for the re-check below.
    let inFlight = null;

    // One round trip through the hidden iframe. Returns its cleanup.
    const runAttempt = () => {
      const token = localStorage.getItem("token");
      const hasLiveToken = hasLiveTokenNow();

      let iframe = document.createElement("iframe");
      let timeoutId = null;

      const cleanup = () => {
        window.removeEventListener("message", handleMessage);
        if (timeoutId) clearTimeout(timeoutId);
        if (iframe) {
          iframe.remove();
          iframe = null;
        }
        inFlight = null;
      };

      const handleMessage = (event) => {
        // Only trust the auto-login page itself.
        if (event.origin !== expectedOrigin) return;
        if (!event.data || event.data.type !== "mybestuser-sso") return;

        const code = event.data.code;
        cleanup();

        if (!code) {
          // Only "not_authenticated" means the tablet genuinely logged out,
          // and only act on it when this browser follows the mybestuser
          // session — otherwise a plain PC user, who has no mybestuser
          // session by design, would be signed out on every visit. Every
          // other verdict must NOT sign anyone out: "server_error" is a
          // transient master fault, "forbidden_context" is the cross-origin
          // guard refusing a read, and a network timeout never posts a
          // message so it never reaches here.
          // `token` — not hasLiveToken — is the "is there anything to clear"
          // test, and it is what stops this from looping. Signing out
          // removes the token, so the next load lands here with none and
          // does nothing; without the check that load would sign out and
          // reload again, and again, because shouldTrackSession stays true
          // on any tablet whose isTablet flag outlives the marker we just
          // dropped. Using `token` rather than hasLiveToken also clears an
          // expired-but-present session, which is the same stale account by
          // a slower route.
          if (
            token &&
            event.data.error === "not_authenticated" &&
            shouldTrackSession()
          ) {
            // Order matters: forget the marker BEFORE signing out. useSignout
            // does not clear it, and a leftover marker would make the same
            // user's next tablet login match knownUserId, short-circuit
            // before the exchange, and strand them on a page whose token is
            // now gone.
            forgetSSOUser();
            // Reload only once the clearing has actually settled, and not at
            // all if it threw: navigating on a half-cleared state is how the
            // loop above would come back, with the token still in place.
            // Full reload, not navigate(): only a fresh load guarantees no
            // Apollo/Redux remnant of the signed-out account survives.
            Promise.resolve()
              .then(() => signoutRef.current())
              .then(() => {
                window.location.href = "/";
              })
              .catch((e) => {
                console.warn("[SSO] sign-out after tablet logout failed:", e);
                // No reload is coming after all — say so, or the landing
                // page would wait on a verdict that never arrives.
                setState("none");
              });
            // Stay "pending": the reload above is the answer, and flipping
            // to "none" first would open the sign-in dialog for a page that
            // is about to go away.
            return;
          }
          // Any other error → stay put; manual sign-in remains available.
          setState("none");
          return;
        }

        const sessionUserId = event.data.userId
          ? String(event.data.userId)
          : null;

        if (hasLiveToken) {
          // Signed in already — only act when the session belongs to someone
          // else. Compare against SSO_USER_KEY, the mybestuser id we recorded
          // the last time we took a session, NOT localStorage "user": that
          // holds the account as the TA server knows it (signinWithCode runs
          // against dev.api.mybestta.com, not mybestuser), so its id lives in
          // a different namespace and never matches — which silently turned
          // every page load into a fresh exchange, each one displacing the
          // session the one before it had just created.
          const knownUserId = localStorage.getItem(SSO_USER_KEY);
          // An endpoint that predates userId gives us nothing to compare.
          if (!sessionUserId) {
            setState("none");
            return;
          }
          // Either the tablet belongs to someone else now, the same user has
          // logged in again since we took our token (which that logout
          // revoked), or we have never recorded who this session is (a
          // manual login, or a page from before this marker existed) — take
          // the session in every one of these cases. That writes the marker,
          // so it happens at most once per user and session.
          // Full page load, NOT navigate(): Apollo and Redux still hold the
          // previous account's data and only a reload guarantees a clean
          // slate.
          const takeSession = () => {
            rememberSSOUser(sessionUserId);
            rememberCurrentLocation();
            // Whatever the tablet pages cached under the old token — possibly
            // empty lists, if that token was already dead — must not outlive
            // it: sessionStorage survives the reload below.
            clearSchoolClassroomData();
            setState("success");
            window.location.href =
              "/mybestuser?code=" + encodeURIComponent(code);
          };

          if (!knownUserId || knownUserId !== sessionUserId) {
            takeSession();
            return;
          }
          if (tokenPredatesSession(event.data.sessionCreatedAt)) {
            takeSession();
            return;
          }
          // Same user, and the session is not newer than our token — or the
          // server did not say when the session was planted. Only in that
          // last case is the identity match not proof enough (see
          // tokenPredatesSession): ask mybestuser whether it still accepts
          // our token before keeping it.
          if (typeof event.data.sessionCreatedAt === "number") {
            setState("none");
            return;
          }
          tokenStillAccepted().then((accepted) => {
            if (accepted) {
              setState("none");
            } else {
              takeSession();
            }
          });
          return;
        }

        // No token: reuse the manual-login tail (MyBestUserLogin route).
        // This branch also serves an expired token, so the page the tablet
        // opened (a deep link, or wherever a recheck found us) must be
        // recorded or MyBestUserLogin lands on the role home instead.
        rememberSSOUser(sessionUserId);
        rememberCurrentLocation();
        setState("success");
        navigate("/mybestuser?code=" + encodeURIComponent(code));
      };

      window.addEventListener("message", handleMessage);

      iframe.src = MYBESTUSER_API + "/api/sso/auto-login";
      iframe.style.display = "none";
      iframe.setAttribute("aria-hidden", "true");
      iframe.title = "sso-auto-login";
      document.body.appendChild(iframe);

      // Giving up is a verdict too: the landing page must not wait forever.
      timeoutId = setTimeout(() => {
        cleanup();
        setState("none");
      }, DISCONNECT_TIMEOUT_MS);

      return cleanup;
    };

    inFlight = runAttempt();

    // Ask again whenever this page comes back into view still signed out.
    // The tablet sign-in happens in another app (tabletauth://open takes the
    // user to tablet-auth-app, whose bridge then plants the cookie), and
    // this very tab — dialog and all — is what they come back to. A single
    // check at load had already answered "not_authenticated" by then, so
    // the page sat on "Please login first" over a session that now existed.
    // A signed-in tablet page whose token came from the tablet session asks
    // too: logging out happens in tablet-auth-app, and the user comes back to
    // a tab that still holds the old token. Without asking, that tab stays
    // signed in — every in-app link is an SPA navigation, so the load-time
    // check never runs again. The live-token branch of the handler is safe to
    // repeat: same user and session → "none", nothing changes;
    // not_authenticated → sign out; another account → hand over.
    // Deliberately narrower than shouldTrackSession: a not_authenticated
    // answer signs the page out, so a PC with a stale isTablet flag (and no
    // tablet session, ever) must not be asked on every tab switch.
    // A manual code exchange in progress on /mybestuser must not be raced.
    // That guard, together with the live-token check above, keeps a taken
    // code from being followed by a second exchange. A failed exchange sends
    // the user back here signed out, and that is exactly when the next return
    // to this tab must ask again.
    const recheck = () => {
      if (document.visibilityState !== "visible") return;
      if (inFlight) return;
      const signedIn = hasLiveTokenNow();
      if (signedIn && !(tokenFromTabletSession() && inTabletBrowser())) return;
      if (window.location.pathname.startsWith("/mybestuser")) return;
      // "pending" tells the landing page a sign-in may be coming; a signed-in
      // page has no dialog waiting on that, so leave its verdict alone.
      if (!signedIn) setState("pending");
      inFlight = runAttempt();
    };
    document.addEventListener("visibilitychange", recheck);
    // A page restored from the back/forward cache fires no visibilitychange.
    window.addEventListener("pageshow", recheck);

    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("pageshow", recheck);
      if (inFlight) inFlight();
    };
    // Run once on mount: the provider never remounts, and a route change
    // right after mount (e.g. Authorization redirecting to "/") must not
    // abort an in-flight attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
};
